import { Box, Flex, FormControl, FormErrorMessage, HStack, StackDivider, useToast, VStack, useDisclosure, Stack } from "@chakra-ui/react";
import CDS from "design-system";
import { MeetingTemplate } from "features/Resources/MeetingTemplate/models";
import { useForm, Controller } from "react-hook-form";
import { useNavigate, useSearchParams } from "react-router";
import { useCreateMeetingTemplate, useUpdateMeetingTemplate } from "features/Resources/MeetingTemplate/hooks";
import { PrimaryButton } from "components/Button";
import { dirtyValues } from "helpers/form";
import NameField from "features/Resources/Shared/Components/Fields/NameField";
import { PEOPLE_TEAM_ROUTES } from "definitions/constants/routeConstants";
import { Path } from "features/Path/models";
import TemplateResourceOptionMenu from "features/Resources/SharedTemplate/Components/TemplateResourceOptionMenu";
import { yupResolver } from "@hookform/resolvers/yup";
import { v2pathPathById } from "helpers/url/pages/v2/pathPath";
import { PropsWithChildren, useEffect, useState, useCallback, useMemo } from "react";
import TagByResourceType from "components/Tag/TagByResourceType";
import ArchivedTag from "components/Tag/ArchivedTag";
import CaminoWarningTriangle from "components/Icon/CaminoWarningTriangle";
import ExternalLink from "components/Link";
import BasePathTag from "components/Tag/BasePathTag";
import SelectPathsMenuButton from "features/Path/Components/Button/SelectPathsMenuButton";
import AttributeColumnLabel from "features/Resources/Shared/Components/AttributeColumnLabel";
import UserRoleOrEmailMultiSelect, {
  UserRoleOrEmailOption,
} from "components/FormElements/Fields/SelectFields/UserRoleOrEmailMultiSelect";
import UserOrChannelSelectField from "components/FormElements/Fields/SelectFields/UserOrChannelSelectField";
import { fromCompositeId } from "helpers/compositeId";
import { chakraComponents } from "chakra-react-select";
import PlusCirlceButton from "components/Button/PlusCircleButton";
import ErrorText from "components/FormElements/ErrorText";
import DefaultErrorText from "components/Form/DefaultErrorText";
import { get } from "lodash";
import { usePathsFieldArray } from "features/Resources/Shared/hooks/usePathsFieldArray";
import OccurrenceSelectionForm from "../Components/Form/OccurrenceSelection";
import TimingForm from "../Components/Form/Timing";
import MeetingTypeSelector from "../Components/MeetingTypeSelector";
import DocumentRichTextField from "../Components/DocumentRichTextField";
import {
  getRecurringMeetingChangedFields,
  shouldShowRecurringMeetingWarning,
  fromUserRoleOrEmailOption,
  toUserRoleOrEmailOption,
  toMeetingTemplateFormData,
} from "../helpers";
import RecurringMeetingWarningModal from "../Components/RecurringMeetingWarningModal";
import { meetingTemplateSchema, MeetingTemplateSchema } from "../schema";

function WarningWrapper({ warn, shouldFill, children }: { warn?: boolean; shouldFill?: boolean } & PropsWithChildren) {
  if (warn) {
    return (
      <Box
        borderWidth="1px"
        borderColor="cdsOrange.100"
        borderRadius="md"
        backgroundColor={shouldFill ? "cdsOrange.100" : undefined}
        px="8px"
        py="4px"
      >
        <HStack>
          <CaminoWarningTriangle stroke="cdsOrange.500" />
          {children}
        </HStack>
      </Box>
    );
  }

  return children;
}

function LeadSelectControl(props: any) {
  const {
    children,
    hasValue,
    selectProps: { customProps },
  } = props;

  if (customProps?.menuIsOpen) {
    return <chakraComponents.Control {...props} />;
  }

  return (
    <chakraComponents.Control {...props}>
      <Flex align="start" justify="flex-start" gap={2} flexWrap="wrap" pl={2} pr={2}>
        {children}
        {!hasValue && (
          <PlusCirlceButton
            mt="2px"
            label="Lead"
            onClick={(e: React.MouseEvent) => {
              e.stopPropagation();
              customProps?.setMenuIsOpen(true);
            }}
          />
        )}
      </Flex>
    </chakraComponents.Control>
  );
}

const defaultMeetingTemplateValues: Partial<MeetingTemplateSchema> = {
  paths: [],
};

function AttendeesSelectWrapper({ value, onChange }: { value: any[]; onChange: (value: any[]) => void }) {
  const selectValue = useMemo(
    () => (value || []).map((formAttendee) => toUserRoleOrEmailOption(formAttendee)),
    [value],
  );
  const updateFormAttendees = useCallback(
    (selectedAttendees: UserRoleOrEmailOption[]) => {
      onChange((selectedAttendees || []).map((selectedAttendee) => fromUserRoleOrEmailOption(selectedAttendee)));
    },
    [onChange],
  );
  return (
    <UserRoleOrEmailMultiSelect
      value={selectValue}
      onChange={(selected) => updateFormAttendees(selected)}
      includePersonal
    />
  );
}

export default function MeetingTemplateForm({ id, data }: { id: string; data: MeetingTemplate }) {
  const toast = useToast();
  const navigate = useNavigate();
  const [leadMenuIsOpen, setLeadMenuIsOpen] = useState(false);
  const [pendingFormData, setPendingFormData] = useState<Partial<MeetingTemplateSchema> | null>(null);
  const { isOpen, onOpen, onClose } = useDisclosure();

  const [searchParams, setSearchParams] = useSearchParams();
  const isNewImport = searchParams.get("import") === "true";
  const isCreate = id === "new";

  const form = useForm<MeetingTemplateSchema>({
    mode: "onChange",
    resolver: yupResolver(meetingTemplateSchema(isNewImport)),
    defaultValues: {
      ...defaultMeetingTemplateValues,
      ...(toMeetingTemplateFormData(data) as any),
    },
  });
  const { mutate: onUpdate } = useUpdateMeetingTemplate(data?.id);
  const { mutate: onCreate } = useCreateMeetingTemplate();
  const {
    handleSubmit,
    control,
    formState: { dirtyFields, errors },
    watch,
  } = form;

  const meetingType = watch("meetingType");

  // Paths field logic - using shared hook
  const { pathFields, removePath, addPathId, selectedPathIds, getPathData, returnPathId } = usePathsFieldArray(form);

  // Paths field: Selected paths components
  const selectedPathsComponents = useMemo(
    () =>
      (pathFields || []).map((field: any, index: number) => {
        const path = getPathData(field.id);
        return (
          <BasePathTag
            key={field.fieldId}
            pathId={path?.id}
            pathName={path?.name}
            icon={path?.icon}
            iconColor={path?.iconColor}
            removeable
            onRemove={() => removePath(index)}
          />
        );
      }),
    [pathFields, getPathData, removePath],
  );

  // Reset form data when data changes
  useEffect(() => {
    if (data?.id) {
      form.reset(toMeetingTemplateFormData(data));
      // If this is a new import and not a recurring session, reset meetingType to force selection
      if (isNewImport && meetingType !== "group_session_recurring") form.setValue("meetingType", undefined);
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [data]);

  const performUpdate = useCallback(
    (updateData: any) => {
      onUpdate(updateData, {
        onSuccess: () => {
          toast({ title: "Meeting template updated.", status: "success" });
          searchParams.delete("import");
          setSearchParams(searchParams);
          setPendingFormData(null);
        },
      });
    },
    [onUpdate, toast, searchParams, setSearchParams],
  );

  const handleModalConfirm = useCallback(() => {
    if (pendingFormData) {
      performUpdate(pendingFormData);
    }
    onClose();
  }, [pendingFormData, performUpdate, onClose]);

  const handleModalClose = useCallback(() => {
    setPendingFormData(null);
    onClose();
  }, [onClose]);

  const onSubmit = (formData: Partial<MeetingTemplateSchema>) => {
    const updateData = isCreate ? formData : dirtyValues(formData, dirtyFields);

    // Early return if updating with no changes
    if (!isCreate && (!updateData || Object.keys(updateData).length === 0)) {
      return;
    }

    if (updateData.paths) {
      updateData.pathIds = ((updateData?.paths || []) as Path[]).map((path) => path.id);
    }

    // Decompose leadCompositeId into leadId/leadType for the API
    if (updateData.leadCompositeId) {
      const { resourceId, resourceType } = fromCompositeId(updateData.leadCompositeId);
      updateData.leadId = resourceId;
      updateData.leadType = resourceType;
    }
    delete updateData.leadCompositeId;

    if (meetingType === "group_session_adhoc" || meetingType === "group_session_recurring") {
      updateData.purpose = undefined;
    } else {
      updateData.learningObjective = undefined;
      updateData.leadType = undefined;
      updateData.leadId = undefined;
    }

    if (isCreate) {
      onCreate(updateData, {
        onSuccess: (res: any) => {
          toast({ title: "Meeting template created.", status: "success" });
          if (returnPathId) {
            navigate(v2pathPathById(returnPathId));
          } else if (formData?.pathIds?.length) {
            navigate(v2pathPathById(formData.pathIds[0]));
          } else {
            navigate(PEOPLE_TEAM_ROUTES.meetingTemplates.index);
          }
        },
      });
    } else {
      const changedFields = getRecurringMeetingChangedFields(dirtyFields, data, formData, meetingType);

      const showWarning = shouldShowRecurringMeetingWarning(
        isCreate,
        data?.meetingType === "group_session_recurring",
        changedFields,
      );

      if (showWarning) {
        setPendingFormData(updateData);
        onOpen();
      } else {
        performUpdate(updateData);
      }
    }
  };

  const changedFieldsForModal = pendingFormData
    ? getRecurringMeetingChangedFields(dirtyFields, data, form.getValues(), meetingType)
    : [];

  const isGroupSession = ["group_session_recurring", "group_session_adhoc"].includes(meetingType);

  const meetingTypeAutofocus = isCreate || (isNewImport && meetingType !== "group_session_recurring");

  return (
    <>
      <VStack spacing={4} align="stretch" width="100%">
        {isNewImport && (
          <WarningWrapper warn shouldFill>
            <CDS.Text12 textColor="content.primary">
              Please select
              <CDS.Text12 as="span" fontWeight="bold">
                {" "}
                the path(s){" "}
              </CDS.Text12>
              where this meeting should live. You can also add additional guests, a leader, and a learning objective
              here.
            </CDS.Text12>
          </WarningWrapper>
        )}
        {/* Top row: tag + menu — full width */}
        <Flex w="100%" justify="space-between" alignItems="center">
          <TagByResourceType type="meeting_template" />
          <HStack>
            {data.recurringEventExternalUrl && (
              <ExternalLink
                w="fit-content"
                text="Google Calendar"
                to={data.recurringEventExternalUrl}
                linkProps={{ target: "_blank" }}
              />
            )}
            {data?.archivedAt && <ArchivedTag />}
            <PrimaryButton
              onClick={handleSubmit(onSubmit, () => {
                toast({
                  title: "Please fix the errors before continuing.",
                  status: "error",
                  duration: 5000,
                });
              })}
            >
              {isCreate ? "Add" : "Save"}
            </PrimaryButton>
            {data && <TemplateResourceOptionMenu excludeTestSend template={data} />}
          </HStack>
        </Flex>

        {/* Name above the attributes row. */}
        <NameField form={form} />

        {/* Template attributes — Type | Schedule | Path, framed top/bottom,
            vertical dividers between, side-by-side at the left. */}
        <HStack
          divider={<StackDivider borderColor="gray.200" />}
          spacing={0}
          py={4}
          borderTop="1px solid"
          borderBottom="1px solid"
          borderColor="gray.200"
          align="stretch"
          justify="flex-start"
        >
          <VStack flex="0 0 auto" spacing={2} align="stretch" px={6} minW="220px">
            <AttributeColumnLabel>Type</AttributeColumnLabel>
            <FormControl isInvalid={!!errors.meetingType}>
              <MeetingTypeSelector
                form={form}
                disabled={!meetingTypeAutofocus}
                includeLabel
                // eslint-disable-next-line jsx-a11y/no-autofocus
                autoFocus={meetingTypeAutofocus}
              />
              <FormErrorMessage mt={0} fontSize="xs">{(errors.meetingType as any)?.message}</FormErrorMessage>
            </FormControl>
          </VStack>
          <VStack flex="0 0 auto" spacing={2} align="stretch" px={6} minW="240px">
            <AttributeColumnLabel>Schedule</AttributeColumnLabel>
            <TimingForm form={form} />
          </VStack>
          {meetingType === "group_session_recurring" && (
            <VStack flex="0 0 auto" spacing={2} align="stretch" px={6} minW="220px">
              <AttributeColumnLabel>Occurrence</AttributeColumnLabel>
              <OccurrenceSelectionForm form={form} />
            </VStack>
          )}
          <VStack flex="0 0 auto" spacing={2} align="stretch" px={6} minW="220px">
            <AttributeColumnLabel>Path</AttributeColumnLabel>
            <WarningWrapper warn={isNewImport}>
              <Flex gap="1" align="center" wrap="wrap">
                {selectedPathsComponents}
                <SelectPathsMenuButton selectedPathIds={selectedPathIds} addPathId={addPathId} />
              </Flex>
            </WarningWrapper>
          </VStack>
        </HStack>

        <Flex align="start" width="100%">
          {/* Meeting content — full width since the attributes row lives above. */}
          <VStack flex="1" spacing={6} align="stretch" minW={0}>
            {/* Lead row (group session only). */}
            {isGroupSession && (
              <FormControl isInvalid={!!errors.leadCompositeId}>
                <VStack spacing={1} align="stretch">
                  <AttributeColumnLabel>Lead</AttributeColumnLabel>
                  <UserOrChannelSelectField
                    name="leadCompositeId"
                    types={["users"]}
                    control={control}
                    maxSelections={1}
                    placeholder="Add a Lead..."
                    variant="unstyled"
                    menuIsOpen={leadMenuIsOpen}
                    openMenuOnClick={false}
                    openMenuOnFocus
                    onMenuOpen={() => setLeadMenuIsOpen(true)}
                    onMenuClose={() => setLeadMenuIsOpen(false)}
                    isClearable={false}
                    components={{
                      DropdownIndicator: null,
                      Control: LeadSelectControl,
                    }}
                    customProps={{
                      menuIsOpen: leadMenuIsOpen,
                      setMenuIsOpen: setLeadMenuIsOpen,
                    }}
                  />
                  <FormErrorMessage mt={0} fontSize="xs">{errors.leadCompositeId?.message}</FormErrorMessage>
                </VStack>
              </FormControl>
            )}

            {/* Attendees row. */}
            <VStack spacing={1} align="stretch">
              <AttributeColumnLabel>Attendees</AttributeColumnLabel>
              <FormControl variant="unstyled" id="attendees">
                <Stack>
                  <Controller
                    control={control}
                    name="attendees"
                    render={({ field: { onChange, value } }) => (
                      <AttendeesSelectWrapper value={value} onChange={onChange} />
                    )}
                  />
                  <ErrorText error={get(form.formState.errors, "attendees")} />
                </Stack>
              </FormControl>
            </VStack>

            <Box display={isGroupSession ? "block" : "none"}>
              <DocumentRichTextField
                name="learningObjective"
                label="Learning Objective"
                placeholder="Add learning objectives, links, or anything attendees should know…"
                control={control}
                reserveToolbarSpace
              />
              {get(form.formState.errors, "learningObjective") && (
                <DefaultErrorText>{(get(form.formState.errors, "learningObjective") as any)?.message}</DefaultErrorText>
              )}
            </Box>
            <Box display={!isGroupSession ? "block" : "none"}>
              <DocumentRichTextField
                name="purpose"
                label="Purpose"
                placeholder="Add talking points, links, or anything the manager should prep…"
                control={control}
                reserveToolbarSpace
              />
              {get(form.formState.errors, "purpose") && (
                <DefaultErrorText>{(get(form.formState.errors, "purpose") as any)?.message}</DefaultErrorText>
              )}
            </Box>
            <DocumentRichTextField
              name="additionalInfo"
              label="Additional Info"
              placeholder="Add a sample agenda, talking points, links, or anything else attendees should know…"
              control={control}
              reserveToolbarSpace
            />
          </VStack>

        </Flex>
      </VStack>

      <RecurringMeetingWarningModal
        isOpen={isOpen}
        onClose={handleModalClose}
        onConfirm={handleModalConfirm}
        changedFields={changedFieldsForModal}
      />
    </>
  );
}
